import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyState, Pager, Spinner, StatTile } from '../components/Layout';
import { PAGE_SIZE } from '../hooks/useCases';
import { security as securityApi, errorMessage } from '../utils/api';
import { formatDateTime, formatNumber } from '../utils/format';

/*
 * دفتر امنیتی — فقط برای مدیر.
 *
 * هدف این صفحه پاسخ دادن به یک سؤال است: «آیا کسی دارد به سامانه دست‌درازی
 * می‌کند؟» پس بالای صفحه خلاصه‌ی ۲۴ ساعت می‌آید و پایینش فهرست خام، تا مدیر
 * از نگاه کلی به جزئیات برسد و نه برعکس.
 */

const EVENT_TONE = {
  brute_force: 'danger',
  throttled: 'warning',
  permission_denied: 'warning',
  login_failed: 'notice',
  not_found_probe: 'notice',
  token_rejected: 'notice',
  login_success: 'ok',
  logout: 'muted',
  file_accessed: 'info',
};

const FILTERS = [
  { id: '', label: 'همه' },
  { id: 'login_failed,brute_force,throttled', label: 'ورود ناموفق و حمله' },
  { id: 'permission_denied,not_found_probe,token_rejected', label: 'دسترسی غیرمجاز' },
  { id: 'file_accessed', label: 'دسترسی به فایل بیمار' },
  { id: 'user_created,user_modified,user_toggled,password_changed,policy_changed', label: 'اقدام‌های مدیریتی' },
];

export default function SecurityTab() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({ ...(filter ? { event: filter } : {}), ...(search ? { q: search } : {}) }),
    [filter, search]
  );

  // دفتر امنیتی فقط افزودنی است و از هر فهرست دیگری تندتر بلند می‌شود؛
  // بدون صفحه‌بندی، رخدادهای قدیمی‌تر از پنجاهمی اصلاً دیده نمی‌شدند.
  useEffect(() => {
    setPage(1);
  }, [params]);

  const load = useCallback(async () => {
    setError('');
    try {
      const [{ data: sum }, { data: list }] = await Promise.all([
        securityApi.summary(),
        securityApi.list({ ...params, page, page_size: PAGE_SIZE }),
      ]);
      setSummary(sum);
      setRows(list.results ?? []);
      setCount(list.count ?? 0);
    } catch (err) {
      setError(errorMessage(err, 'دفتر امنیتی بارگذاری نشد.'));
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <>
      {summary && (
        <div className="tiles">
          <StatTile
            label="ورود ناموفق (۲۴ ساعت)"
            value={formatNumber(summary.login_failed_24h)}
            tone={summary.login_failed_24h > 0 ? 'warning' : 'default'}
          />
          <StatTile
            label="رخداد بحرانی (۷ روز)"
            value={formatNumber(summary.critical_7d)}
            tone={summary.critical_7d > 0 ? 'danger' : 'success'}
            hint={summary.critical_7d > 0 ? 'نیاز به بررسی دارد' : 'چیزی دیده نشده'}
          />
          <StatTile
            label="دسترسی غیرمجاز (۲۴ ساعت)"
            value={formatNumber(summary.denied_24h)}
            tone={summary.denied_24h > 0 ? 'warning' : 'default'}
          />
          <StatTile label="ورود موفق (۲۴ ساعت)" value={formatNumber(summary.login_success_24h)} tone="brand" />
          <StatTile
            label="دسترسی به فایل بیمار (۲۴ ساعت)"
            value={formatNumber(summary.file_access_24h)}
            tone="info"
          />
        </div>
      )}

      {summary?.top_failed_ips?.length > 0 && (
        <section className="panel">
          <h2 className="panel__title">نشانی‌هایی که بیشترین ورود ناموفق را داشته‌اند</h2>
          <p className="panel__hint">
            اگر نشانی‌ای اینجا با عدد بالا دیدید که برای کارکنان شما نیست، احتمالاً
            کسی در حال حدس زدن رمز است. سامانه پس از عبور از آستانه خودش آن نشانی را
            موقتاً می‌بندد، ولی دیدنش به شما می‌گوید چه می‌گذرد.
          </p>
          <div className="ipbars">
            {summary.top_failed_ips.map((row) => (
              <div key={row.ip} className="ipbar">
                <span className="num ipbar__ip" dir="ltr">{row.ip}</span>
                <span className="ipbar__track">
                  <span
                    className="ipbar__fill"
                    style={{
                      width: `${Math.min(
                        (row.count / (summary.top_failed_ips[0].count || 1)) * 100,
                        100
                      )}%`,
                    }}
                  />
                </span>
                <span className="num ipbar__count">{formatNumber(row.count)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="toolbar">
        <select className="input" value={filter} onChange={(event) => setFilter(event.target.value)}>
          {FILTERS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <input
          type="search"
          className="input input--search"
          placeholder="جست‌وجوی نام کاربری، نشانی یا مسیر…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
          ↻ تازه‌سازی
        </button>
      </div>

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && !error && rows.length === 0 && (
        <EmptyState icon="🔐" title="رخدادی با این فیلتر ثبت نشده است." />
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>زمان</th>
                <th>رخداد</th>
                <th>کاربر</th>
                <th>نشانی</th>
                <th>مسیر</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className={row.severity === 'critical' ? 'row--critical' : ''}>
                    <td><time>{formatDateTime(row.created_at)}</time></td>
                    <td>
                      <span className={`sev sev--${EVENT_TONE[row.event] || 'muted'}`}>
                        {row.event_display}
                      </span>
                    </td>
                    <td>
                      {row.user_name || (
                        row.username_attempted
                          ? <span className="muted">{row.username_attempted} (ناموفق)</span>
                          : <span className="muted">—</span>
                      )}
                    </td>
                    <td><span className="num" dir="ltr">{row.ip || '—'}</span></td>
                    <td className="cell--path" dir="ltr">{row.path || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                      >
                        {expanded === row.id ? 'بستن' : 'جزئیات'}
                      </button>
                    </td>
                  </tr>
                  {expanded === row.id && (
                    <tr>
                      <td colSpan={6} className="cell--detail">
                        <dl className="kv">
                          <div><dt>شدت</dt><dd>{row.severity_display}</dd></div>
                          <div><dt>روش</dt><dd className="num" dir="ltr">{row.method || '—'}</dd></div>
                          <div><dt>کد پاسخ</dt><dd className="num">{row.status_code ?? '—'}</dd></div>
                          <div className="kv__wide">
                            <dt>مرورگر</dt>
                            <dd dir="ltr" className="kv__ua">{row.user_agent || '—'}</dd>
                          </div>
                        </dl>
                        {Object.keys(row.detail || {}).length > 0 && (
                          <pre className="detail-json" dir="ltr">
                            {JSON.stringify(row.detail, null, 2)}
                          </pre>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} رخداد در این فیلتر</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={PAGE_SIZE}
        count={count}
        onChange={setPage}
      />

      <p className="form__note">
        دفتر امنیتی ویرایش و حذف نمی‌شود. یک نسخه‌ی JSON از همه‌ی این رخدادها در
        فایل <code>logs/security.log</code> روی سرور هم نوشته می‌شود تا اگر پایگاه
        داده از دست رفت، رد پا جای دیگری مانده باشد.
      </p>
    </>
  );
}
